import { useQueryClient } from '@tanstack/react-query';

import { useFlag } from '@simplywallst/unleash';

import { gql } from '@/utilities/gql';
import { makeGqlMutation } from '@/utilities/gqlQuery';

import type { VariablesOf } from '@/utilities/gql';

import { QUERY_KEY as DASHBOARD_FEED } from '@/components/Dashboard/hooks/useFeed';

import type { PortfolioQueryClientData } from '@/hooks/portfolio/usePortfolio';
import {
  PORTFOLIO_QUERY_KEY,
  PORTFOLIO_FRAGMENT,
  PORTFOLIOS_QUERY_KEY,
} from '@/hooks/portfolio/usePortfolio';
import { AGGREGATE_PORTFOLIO_QUERY_KEY } from '@/hooks/portfolio/useAggregatePortfolio';
import { PORTFOLIOS_PERFORMANCE_TIMESERIES_QUERY_KEY } from '@/hooks/portfolio/usePortfolioPerformanceTimeseries';

const MUTATION = gql(
  `
  mutation addTransactionToPortfolio($id: String!, $transaction: ManualTransactionInput!, $triggerRecalculation: Boolean) {
    addTransactionToPortfolio(id: $id, transaction: $transaction, triggerRecalculation: $triggerRecalculation) {
      ...portfolioFields
    }
  }
`,
  [PORTFOLIO_FRAGMENT]
);

export type TransactionInput = VariablesOf<typeof MUTATION>['transaction'];

const mutation = makeGqlMutation(MUTATION);

export const useAddTransactionToPortfolio = () => {
  const willDeprecateStream = useFlag('stream-from-database');

  const queryClient = useQueryClient();
  return mutation({
    onSuccess: (data, { id: portfolioId }) => {
      const portfolio = data.addTransactionToPortfolio;
      queryClient.setQueryData<PortfolioQueryClientData>(
        [PORTFOLIO_QUERY_KEY, portfolioId],
        () => portfolio
      );
    },
    onSettled: (_, __, { id }) => {
      if (willDeprecateStream.enabled) {
        // Invalidate the dashboard feed query to ensure it reflects the latest data
        // e.g. it includes all the companies/holdings that should be in the feed
        queryClient.invalidateQueries({ queryKey: DASHBOARD_FEED });
      }
      queryClient.invalidateQueries({
        queryKey: [PORTFOLIO_QUERY_KEY, id],
      });
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: AGGREGATE_PORTFOLIO_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: [PORTFOLIOS_PERFORMANCE_TIMESERIES_QUERY_KEY, id],
      });
    },
  });
};
